import { AccordionItemProps } from '@vaad1st/component.molecule.accordion-item';
import { ContentCardProps } from '@vaad1st/component.molecule.content-card';
import { GalleryCardProps } from '@vaad1st/component.molecule.gallery-card';
import { TextLockupProps } from '@vaad1st/component.molecule.text-lockup';
import { AccordionComponent as Accordion } from '@vaad1st/component.organism.accordion';
import { VAAContext } from 'pages/_app';
import { useContext } from 'react';
import { removeHTMLTags } from 'src/utils';
import { HeadingElementType } from 'src/utils/shared-types';
import ContentCardComponent from '../content-card/content-card.component';
import GalleryCardComponent from '../gallery-card/gallery-card.component';
import JsonLd from '../json-ld/json-ld.component';
import StoreFinder, {
  StoreFinderComponentProps,
} from '../store-finder/store-finder.component';
import SVGRenderer from '../svg-renderer/svg-renderer.component';
import TableCard, { TableCardProps } from '../table-card/table-card.component';
import TextLockupComponent from '../text-lockup/text-lockup.component';
import VideoComponent, {
  VideoComponentProps,
} from '../video-component/video-component.component';

type ComponentProps =
  | ContentCardProps
  | TextLockupProps
  | GalleryCardProps
  | TableCardProps
  | VideoComponentProps
  | StoreFinderComponentProps;

export type AEMAccordionItemProps = Omit<
  AccordionItemProps,
  'content' | 'icon'
> & { content: ComponentProps[]; icon?: string };

interface AccordionProps {
  items: AEMAccordionItemProps[];
  heading: string;
  headingElement?: HeadingElementType;
  includeJSONLD?: boolean;
}

export default function AccordionComponent({
  heading,
  headingElement = 'h2',
  items,
  includeJSONLD = false,
}: AccordionProps) {
  const { accordionAnchor } = useContext(VAAContext);

  if (!items) {
    return null;
  }

  // Mutate accordion item content props into actual components
  const accordionItems: AccordionItemProps[] = items.map(
    (accordionItem: AEMAccordionItemProps, accordionItemIndex: number) => {
      const item = {
        ...accordionItem,
        isOpen: accordionAnchor === accordionItem.id,
        icon: accordionItem.icon ? (
          <SVGRenderer svgString={accordionItem.icon} />
        ) : undefined,
      };

      const content = item.content.map(
        (contentItem: any, contentItemIndex: number) => {
          const contentItemType = contentItem['sling:resourceType']
            .split('/')
            .pop();
          switch (contentItemType) {
            case 'text-lockup-component':
              const { children } = contentItem;
              return (
                <TextLockupComponent
                  key={`tlc--${accordionItemIndex}--${contentItemIndex}`}
                  noPadding={true}
                  {...contentItem}>
                  {children}
                </TextLockupComponent>
              );
            case 'content-card':
              return (
                <ContentCardComponent
                  key={`cc--${accordionItemIndex}--${contentItemIndex}`}
                  {...contentItem}
                />
              );
            case 'destination-card':
              return (
                <GalleryCardComponent
                  key={`gcc--${accordionItemIndex}--${contentItemIndex}`}
                  {...contentItem}
                />
              );
            case 'table-card':
              return (
                <TableCard
                  key={`tcc--${accordionItemIndex}--${contentItemIndex}`}
                  {...contentItem}
                />
              );
            case 'video-component':
              return (
                <VideoComponent
                  key={`tcc--${accordionItemIndex}--${contentItemIndex}`}
                  {...contentItem}
                />
              );
            case 'store-finder':
              return (
                <StoreFinder
                  key={`tcc--${accordionItemIndex}--${contentItemIndex}`}
                  {...contentItem}
                />
              );
            default:
              return <></>;
          }
        },
      );

      return {
        ...item,
        content: content,
      };
    },
  );

  return (
    <>
      <Accordion
        heading={heading}
        items={accordionItems as any}
        headingElement={headingElement}
      />
      {includeJSONLD && (
        <JsonLd
          data={{
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: items.map(item => ({
              '@type': 'Question',
              name: item.collapsedLabel,
              acceptedAnswer: {
                '@type': 'Answer',
                text: item.content
                  .map((contentItem: any) => {
                    const contentItemType = contentItem['sling:resourceType']
                      .split('/')
                      .pop();
                    switch (contentItemType) {
                      case 'content-card':
                        return contentItem.text;
                      case 'destination-card':
                        return contentItem.text;
                      case 'table-card':
                        return contentItem.text;
                      case 'text-lockup-component':
                        return removeHTMLTags(contentItem.children);
                      case 'video-component':
                        return contentItem.videoDescription;
                      default:
                        return '';
                    }
                  })
                  .join(' '),
              },
            })),
          }}
        />
      )}
    </>
  );
}
